'use client';

import { useMemo } from 'react';

import { Accordion, Badge } from '@/shared/components/ui';
import { formatAgentVariableLabel } from '@/shared/utils/agentVariables';

interface FlowDefinitionSummary {
  metadata?: {
    steps?: string[];
  };
}

interface FlowSummary {
  name?: string | null;
  description?: string | null;
  definition?: FlowDefinitionSummary | null;
}

interface NegociacaoDetailFlowSectionProps {
  flow: FlowSummary | null;
  isFlowLoading: boolean;
  agentInput: Record<string, unknown> | null;
  presentation?: 'accordion' | 'sections';
}

export function NegociacaoDetailFlowSection({
  flow,
  isFlowLoading,
  agentInput,
  presentation = 'accordion',
}: NegociacaoDetailFlowSectionProps) {
  const flowSteps = useMemo(() => flow?.definition?.metadata?.steps ?? [], [flow]);

  const filteredAgentInput = useMemo(
    () =>
      Object.entries(agentInput ?? {}).filter(
        ([, value]) => value != null && String(value).trim() !== '',
      ),
    [agentInput],
  );

  if (presentation === 'sections') {
    return (
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-1">
          <h4 className="text-subtext-color text-xs font-medium">Fluxo da negociação</h4>
          <p className="text-default-font text-sm font-semibold">
            {isFlowLoading ? 'Carregando fluxo…' : (flow?.name ?? '—')}
          </p>
          <p className="text-subtext-color text-xs leading-relaxed">
            {flow?.description ?? 'Sem descrição'}
          </p>
        </section>

        {filteredAgentInput.length > 0 && (
          <section className="flex flex-col gap-2 border-t border-neutral-100 pt-4">
            <h4 className="text-brand-700 text-xs font-semibold">Parâmetros do agente</h4>
            {filteredAgentInput.map(([key, value]) => (
              <div key={key} className="rounded-[8px] border border-neutral-200 bg-neutral-50 p-3">
                <span className="text-subtext-color block text-xs">
                  {formatAgentVariableLabel(key)}
                </span>
                <span className="text-default-font mt-0.5 block text-sm font-medium break-words">
                  {String(value)}
                </span>
              </div>
            ))}
          </section>
        )}

        <section className="flex flex-col gap-2 border-t border-neutral-100 pt-4">
          <h4 className="text-brand-700 text-xs font-semibold">Etapas do fluxo</h4>
          {isFlowLoading ? (
            <p className="text-subtext-color text-xs">Carregando etapas...</p>
          ) : flowSteps.length === 0 ? (
            <p className="text-subtext-color text-xs">Este fluxo não possui etapas.</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {flowSteps.map((step, index) => (
                <li key={`${step}-${index}`} className="flex items-start gap-2 text-sm">
                  <span className="text-brand-700 shrink-0 text-xs font-semibold">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-default-font break-words">{step}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    );
  }

  return (
    <>
      <Accordion
        defaultOpen={false}
        trigger={
          <div className="flex w-full items-center justify-between py-1">
            <span className="text-caption-bold font-caption-bold text-subtext-color tracking-wider uppercase">
              Flow da negociação
            </span>
            <Accordion.Chevron />
          </div>
        }
      >
        <div className="flex w-full flex-col gap-4 pt-2">
          <div className="flex flex-col gap-1">
            <span className="text-caption text-subtext-color">Nome do flow</span>
            <span className="text-body text-default-font font-medium">{flow?.name ?? '—'}</span>
          </div>

          <p className="text-caption text-subtext-color">{flow?.description ?? 'Sem descrição'}</p>
        </div>
      </Accordion>

      {filteredAgentInput.length > 0 && (
        <Accordion
          defaultOpen={false}
          trigger={
            <div className="flex w-full items-center justify-between py-1">
              <span className="text-caption-bold font-caption-bold text-subtext-color tracking-wider uppercase">
                Parâmetros do Agente
              </span>
              <Accordion.Chevron />
            </div>
          }
        >
          <div className="flex w-full flex-col gap-3 pt-2">
            {filteredAgentInput.map(([key, value]) => (
              <div
                key={key}
                className="flex flex-col gap-1 rounded-[12px] border border-solid border-neutral-200 bg-white p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {formatAgentVariableLabel(key)}
                  </span>
                  <Badge variant="neutral" className="border-neutral-200 bg-neutral-50">
                    {String(value)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Accordion>
      )}

      <Accordion
        defaultOpen={false}
        trigger={
          <div className="flex w-full items-center justify-between py-1">
            <span className="text-caption-bold font-caption-bold text-subtext-color tracking-wider uppercase">
              Etapas do flow
            </span>
            <Accordion.Chevron />
          </div>
        }
      >
        <div className="flex w-full flex-col gap-3 pt-2">
          {isFlowLoading ? (
            <div className="text-caption text-subtext-color">Carregando etapas...</div>
          ) : flowSteps.length === 0 ? (
            <div className="text-caption text-subtext-color">Este flow não possui etapas.</div>
          ) : (
            flowSteps.map((step, index) => (
              <div
                key={`${step}-${index}`}
                className="flex flex-col gap-2 rounded-[12px] border border-solid border-neutral-200 bg-white p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-600">
                    {index + 1}
                  </div>
                  <span className="text-body-bold font-body-bold text-default-font">{step}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </Accordion>
    </>
  );
}
